import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Compare as CompareIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';

const DashboardLayout = () => {
  const { t } = useTranslation();
  const { project, authToken, language } = useStore();
  const [loading, setLoading] = useState(false);

  dayjs.locale(language);

  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    // Formatear fecha manteniendo zona horaria local (no UTC)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatLocalDate(sevenDaysAgo),
      endDate: formatLocalDate(today)
    };
  };

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalReports: 0,
    totalApplicants: 0,
    totalPositions: 0,
    totalEvaluations: 0,
    totalComparisons: 0,
    topUsers: [],
    topPositions: []
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (project && authToken) {
      loadMetrics();
    }
  }, [project, authToken]);

  const loadMetrics = async () => {
    setLoading(true);
    setError('');

    try {
      // Ejecutar todas las consultas en paralelo
      const [
        usersResponse,
        reportsResponse,
        applicantsResponse,
        positionsResponse,
        evaluationsResponse,
        comparisonsResponse
      ] = await Promise.all([
        // Usuarios del proyecto - NUEVA API
        api.get(`/users/by-project`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Reportes del proyecto con filtros de fecha
        api.get(`/reports`, {
          params: {
            projectId: project,
            ...(startDate && { startDate }),
            ...(endDate && { endDate })
          },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Postulantes del proyecto
        api.get(`/applicants`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Posiciones del proyecto
        api.get(`/positions`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Evaluaciones del proyecto
        api.get(`/evaluations`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Comparaciones del proyecto con filtros de fecha - API específica
        api.get(`/comparisons/by-project`, {
          params: {
            projectId: project,
            ...(startDate && { startDate }),
            ...(endDate && { endDate })
          },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        })
      ]);

      // Función helper para parsear respuestas de manera robusta
      const parseResponse = (response, apiName) => {
        // Caso 1: errorCode === 0 y data en response.data
        if (response.data?.errorCode === 0 && response.data?.response?.data) {
          const data = response.data.response.data;

          // Si data es un array, devolverlo directamente
          if (Array.isArray(data)) {
            return data;
          }

          // Si data es un objeto único (como en Users), convertirlo a array
          if (typeof data === 'object' && data !== null) {
            return [data];
          }
        }

        // Caso 2: data directamente en response.data.data
        if (response.data?.data && Array.isArray(response.data.data)) {
          return response.data.data;
        }

        // Caso 3: resultCode 200 y data en response.data
        if (response.data?.resultCode === 200 && response.data?.response?.data) {
          const data = response.data.response.data;
          return Array.isArray(data) ? data : [data];
        }

        return [];
      };

      // Parsear datos correctamente
      const users = parseResponse(usersResponse, 'Users');
      const reports = parseResponse(reportsResponse, 'Reports');
      const applicants = parseResponse(applicantsResponse, 'Applicants');
      const positions = parseResponse(positionsResponse, 'Positions');
      const evaluations = parseResponse(evaluationsResponse, 'Evaluations');
      const comparisons = parseResponse(comparisonsResponse, 'Comparisons');

      // Calcular métricas
      const calculatedMetrics = calculateMetrics(users, reports, applicants, positions, evaluations, comparisons);
      setMetrics(calculatedMetrics);

    } catch (err) {
      console.error('Error loading metrics:', err);
      setError(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (users, reports, applicants, positions, evaluations, comparisons) => {
    // Asegurar que todos los parámetros sean arrays
    const safeUsers = Array.isArray(users) ? users : [];
    const safeReports = Array.isArray(reports) ? reports : [];
    const safeApplicants = Array.isArray(applicants) ? applicants : [];
    const safePositions = Array.isArray(positions) ? positions : [];
    const safeEvaluations = Array.isArray(evaluations) ? evaluations : [];
    const safeComparisons = Array.isArray(comparisons) ? comparisons : [];

    // Contar reportes por usuario
    const userReportCount = {};
    // Contar reportes por posición
    const positionReportCount = {};

    safeReports.forEach(report => {
      // Conteo por usuario
      const userId = report.uid;
      if (userId) {
        userReportCount[userId] = (userReportCount[userId] || 0) + 1;
      }

      // Conteo por posición
      const positionId = report.position_id;
      if (positionId) {
        positionReportCount[positionId] = (positionReportCount[positionId] || 0) + 1;
      }
    });

    // Crear lista de todos los usuarios con sus reportes (incluso si tienen 0)
    const allUsersWithReports = safeUsers.map(user => ({
      userId: user.uid || user.id || 'unknown',
      name: user.name || user.displayName || t('dashboard.tables.unknownUser'),
      email: user.email || t('dashboard.tables.noEmail'),
      reportCount: userReportCount[user.uid] || userReportCount[user.id] || 0
    }));

    // Ordenar por cantidad de reportes descendente
    const topUsers = allUsersWithReports
      .sort((a, b) => b.reportCount - a.reportCount)
      .slice(0, 10); // Top 10 usuarios

    // Crear lista de posiciones con más reportes
    const positionsWithReports = Object.entries(positionReportCount)
      .map(([positionId, count]) => {
        const position = safePositions.find(p => p.uid === positionId);
        return {
          positionId,
          name: position?.name || t('dashboard.tables.unknownPosition'),
          industria: position?.industria || t('dashboard.tables.noIndustry'),
          reportCount: count
        };
      })
      .sort((a, b) => b.reportCount - a.reportCount)
      .slice(0, 10); // Top 10 posiciones

    const metrics = {
      totalUsers: safeUsers.length,
      totalReports: safeReports.length,
      totalApplicants: safeApplicants.length,
      totalPositions: safePositions.length,
      totalEvaluations: safeEvaluations.length,
      totalComparisons: safeComparisons.length,
      topUsers,
      topPositions: positionsWithReports
    };

    return metrics;
  };

  // Función para aplicar filtros
  const applyFilters = () => {
    loadMetrics();
  };

  const MetricCard = ({ title, value, icon: Icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              color="text.primary"
              sx={{ fontWeight: 'bold' }}
            >
              {value}
            </Typography>
          </Box>
          <Icon sx={{ fontSize: 50, color: `${color}.main`, opacity: 0.6 }} />
        </Box>
      </CardContent>
    </Card>
  );

  if (!project) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {t('dashboard.selectProject')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          {t('dashboard.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={loadMetrics}
          disabled={loading}
        >
          {loading ? t('dashboard.loading') : t('dashboard.refresh')}
        </Button>
      </Box>

      {/* Filtros de fecha */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FilterIcon color="primary" />
            <Typography variant="h6">
              {t('dashboard.filters.title')}
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={language}
              >
                <DatePicker
                  label={t('dashboard.filters.startDate')}
                  value={startDate ? dayjs(startDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const formattedDate = newValue.format('YYYY-MM-DD');
                      setStartDate(formattedDate);
                    } else {
                      setStartDate('');
                    }
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={language}
              >
                <DatePicker
                  label={t('dashboard.filters.endDate')}
                  value={endDate ? dayjs(endDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const formattedDate = newValue.format('YYYY-MM-DD');
                      setEndDate(formattedDate);
                    } else {
                      setEndDate('');
                    }
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant="contained"
                onClick={applyFilters}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <FilterIcon />}
                size="large"
                fullWidth
              >
                {t('dashboard.filters.applyFilters')}
              </Button>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('dashboard.filters.showingDataFrom', { startDate, endDate })}
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Métricas principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title={t('dashboard.metrics.totalUsers')}
            value={metrics.totalUsers}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title={t('dashboard.metrics.totalReports')}
            value={metrics.totalReports}
            icon={AssessmentIcon}
            color="secondary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title={t('dashboard.metrics.totalApplicants')}
            value={metrics.totalApplicants}
            icon={PersonIcon}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title={t('dashboard.metrics.totalPositions')}
            value={metrics.totalPositions}
            icon={WorkIcon}
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title={t('dashboard.metrics.totalEvaluations')}
            value={metrics.totalEvaluations}
            icon={AssignmentIcon}
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title={t('dashboard.metrics.totalComparisons')}
            value={metrics.totalComparisons}
            icon={CompareIcon}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Tabla de usuarios con más reportes */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.tables.topUsersTitle')}
            <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 1 }}>
              {t('dashboard.filters.dateRange', { startDate, endDate })}
            </Typography>
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard.tables.position')}</TableCell>
                  <TableCell>{t('dashboard.tables.name')}</TableCell>
                  <TableCell>{t('dashboard.tables.email')}</TableCell>
                  <TableCell align="right">{t('dashboard.tables.reportsCount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.topUsers.map((user, index) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <Typography
                        variant="h6"
                        color={index < 3 ? 'primary' : 'textSecondary'}
                        fontWeight="bold"
                      >
                        #{index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body1"
                        fontWeight={index === 0 ? 'bold' : 'normal'}
                      >
                        {user.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="h6"
                        color={index === 0 ? 'primary' : 'textPrimary'}
                        fontWeight="bold"
                      >
                        {user.reportCount}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {metrics.topUsers.length === 0 && (
            <Typography variant="body2" color="textSecondary" align="center" py={3}>
              {t('dashboard.tables.noUsersData')}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Tabla de posiciones con más reportes */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.tables.topPositionsTitle')}
            <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 1 }}>
              {t('dashboard.filters.dateRange', { startDate, endDate })}
            </Typography>
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard.tables.position')}</TableCell>
                  <TableCell>{t('dashboard.tables.positionName')}</TableCell>
                  <TableCell>{t('dashboard.tables.industry')}</TableCell>
                  <TableCell align="right">{t('dashboard.tables.reportsCount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.topPositions && metrics.topPositions.length > 0 ? (
                  metrics.topPositions.map((position, index) => (
                    <TableRow key={position.positionId}>
                      <TableCell>
                        <Typography
                          variant="h6"
                          color={index < 3 ? 'secondary' : 'textSecondary'}
                          fontWeight="bold"
                        >
                          #{index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body1"
                          fontWeight={index === 0 ? 'bold' : 'normal'}
                        >
                          {position.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {position.industria}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="h6"
                          color={index === 0 ? 'secondary' : 'textPrimary'}
                          fontWeight="bold"
                        >
                          {position.reportCount}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary" align="center" py={3}>
                        {t('dashboard.tables.noPositionsData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardLayout;